import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { Box, Button, CircularProgress, Paper, Table, TableBody, TableContainer, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import Fetch from "../../services/Fetch";
import SnackbarAlert from "../../components/SnackBar";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CourseDetails from "../../popup/CourseDetails";
import CoursesFilter from "../../popup/CoursesFilter";
import { useCoursesFilter } from "../../filter/UseCoursesFilter";
import { usePopups } from "../../hooks/UsePopups";
import { useTableStyles } from "../../hooks/UseTableStyles";
import { useConstants } from "../../hooks/UseConstants";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearch } from "../../hooks/UseSearch";
import { usePagination } from "../../hooks/UsePagination";
import AdminPageHero from "../../components/AdminPageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageContent from "../../components/PageContent";
import ScrollReveal from "../../components/motion/ScrollReveal";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";
import ModalOverlay from "../../components/ModalOverlay";

function Courses() {
    const intl = useIntl();
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { setPopup } = usePopups();
    const { host, language } = useConstants();
    const { StyledTableCell, StyledTableRow } = useTableStyles();
    const { search, setSearch, order, setOrder } = useSearch();
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, filterWait, setFilterWait } = useWaits();
    const { category, setCategory, categoriesValue, setCategoriesValue, path, setPath, pathsValue, setPathsValue, fromCount, setFromCount, toCount, setToCount, fromDate, setFromDate, toDate, setToDate } = useCoursesFilter();
    const { page, setPage, currentPage, setCurrentPage, totalPages, setTotalPages } = usePagination();
    const [coursesCounts, setCoursesCounts] = useState('');
    const [courses, setCourses] = useState([]);
    const [course, setCourse] = useState('');

    const buildCoursesQuery = () => {
        const parts = [`page=${page + 1}`, `search=${encodeURIComponent(search)}`];
        if (order) parts.push(order);
        if (category) parts.push(`category_id=${category}`);
        if (path) parts.push(`path_id=${path}`);
        if (fromCount) parts.push(`files_number[from]=${fromCount}`);
        if (toCount) parts.push(`files_number[to]=${toCount}`);
        if (fromDate) parts.push(`from=${fromDate}`);
        if (toDate) parts.push(`to=${toDate}`);
        return parts.join('&');
    };

    const getCourses = async () => {
        const result = await Fetch(host + `/courses?${buildCoursesQuery()}`, 'GET', null);

        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            setTotalPages(pagination.last_page);
            setCoursesCounts(pagination.total);
            setCourses(items);
            setCurrentPage(page);
        } else {
            setCourses([]);
            setCoursesCounts(0);
        }

        setGetWait(false);
    };

    const loadCourseDetails = async (id, pathId) => {
        const result = await Fetch(`${host}/courses/${id}/show`, 'GET', null);
        if (result.status === 200) {
            const courseDetails = result.data.data;
            const pathItem = courseDetails?.paths?.find((item) => item.id === pathId) || null;
            setCourse({ course: courseDetails, path: pathItem });
            setPopup('details', 'flex');
            return;
        }
        const fallback = courses.find((item) => item.id === id);
        if (fallback) {
            const pathItem = fallback?.paths?.find((item) => item.id === pathId) || null;
            setCourse({ course: fallback, path: pathItem });
            setPopup('details', 'flex');
        }
    };

    const deleteCourse = async (courseId) => {
        const result = await Fetch(`${host}/admin/courses/${courseId}/delete`, 'DELETE', null);
        if (result.status === 200) {
            setCourses((list) => list.filter((item) => item.id !== courseId));
            setPopup('details', 'none');
            setSnackBar('success', intl.formatMessage({ id: 'course_deleted_msg' }));
        } else {
            setSnackBar('error', result.data?.message || result.data?.errors?.[0] || intl.formatMessage({ id: 'error' }));
        }
    };

    const getCourseDetails = (id, pathId) => {
        loadCourseDetails(id, pathId);
    };

    {/* Filtering Courses Function */}
    const filteringCourses = async () => {
        const result = await Fetch(host + `/courses?${buildCoursesQuery()}`, 'GET', null);

        if (result.status === 200) {
            const { items, pagination } = parsePaginatedResponse(result);
            setTotalPages(pagination.last_page);
            setCoursesCounts(pagination.total);
            setCourses(items);
            setPopup('filter', 'none');
        }

        setFilterWait(false);
    }

    useEffect(() => {
        if (!wait) {
            setGetWait(true);
            getCourses();
        }
    }, [wait, page, search, order]);

    const statusLabel = (status) => {
        if (status === 'accepted') return <FormattedMessage id="approved" />;
        if (status === 'pending') return <FormattedMessage id="wait_approval" />;
        if (status === 'rejected') return <FormattedMessage id="rejected" />;
        return status;
    };

    return (
        <>
            <PageContent wait={wait} loading={getWait && !wait}>
                <AdminPageHero />
                <ScrollReveal>
                    <DashboardPanelCard className="mx-0 !mt-0">
                                            <TableContainer component={Paper} dir={language === 'en' ? 'ltr' : "rtl"} sx={{ boxShadow: 'none' }}>
                                                {/* Top Table */}
                                                <Box className="min-h-12 py-2 px-2 flex justify-between items-center max-sm:flex-col">
                                                    <Box className="w-full flex items-center">
                                                        <FilterAltOutlinedIcon onClick={() => setPopup('filter', 'flex')} className="cursor-pointer" fontSize="large" />
                                                        <Box className="w-2/4 relative mr-3 max-sm:w-full">
                                                            <input style={{ backgroundColor: theme.palette.background.default }} onChange={(e) => setSearch(e.target.value)} className="w-10/12 h-12 rounded-md border indent-14 outline-none max-sm:w-full" placeholder={intl.formatMessage({ id: "search_course_teacher_name" })} />
                                                            <SearchOutlinedIcon className="absolute top-1/2 -translate-y-1/2 right-3 text-gray-500" sx={{ right: language === 'en' && '90%' }} />
                                                        </Box>
                                                    </Box>
                                                    <Box className="flex w-2/4 items-center max-sm:mt-2 max-sm:w-full max-sm:justify-between">
                                                        <select onChange={(e) => setOrder(e.target.value)} style={{ backgroundColor: theme.palette.background.select }} className="w-2/5 py-1 rounded-lg mx-3 outline-none">
                                                            <option value=''><FormattedMessage id='date' /></option>
                                                            <option value={language === 'en' ? 'order_by=name_en&direction=asc' : 'order_by=name_ar&direction=asc'}><FormattedMessage id='course_name' /></option>
                                                            <option value='order_by=teacher.name&direction=asc'><FormattedMessage id='teacher_name' /></option>
                                                        </select>
                                                        <Typography variant="body1" className="!text-gray-500"><FormattedMessage id='total_courses' />: {coursesCounts}</Typography>
                                                    </Box>
                                                </Box>

                                                {/* Courses Table */}
                                                <Table className="" sx={{ minWidth: 700 }} aria-label="customized table">
                                                    <TableHead className="bg-gray-200">
                                                        <TableRow sx={{ backgroundColor: theme.palette.background.paper }}>
                                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id='course_name' /></StyledTableCell>
                                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id='teacher_name' /></StyledTableCell>
                                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id='category' /></StyledTableCell>
                                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id='status' /></StyledTableCell>
                                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'} className=""><FormattedMessage id='education_path' /></StyledTableCell>
                                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'}><FormattedMessage id='course_files_count' /></StyledTableCell>
                                                            <StyledTableCell align={language === 'en' ? 'left' : 'right'} className="!text-center"><FormattedMessage id='publication_date' /></StyledTableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {courses.length === 0 ? (
                                                            <StyledTableRow>
                                                                <StyledTableCell colSpan={7} align="center">
                                                                    <FormattedMessage id="nothing" />
                                                                </StyledTableCell>
                                                            </StyledTableRow>
                                                        ) : (
                                                            courses.map((course) =>
                                                                (course.paths?.length ? course.paths : [null]).map((pathItem, pathIdx) => (
                                                                    <StyledTableRow
                                                                        key={`${course.id}-${pathIdx}`}
                                                                        className="hover:bg-gray-100 cursor-pointer"
                                                                        onClick={() => getCourseDetails(course.id, pathItem?.id ?? null)}
                                                                    >
                                                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'} component="th" scope="row">
                                                                            {language === 'en' ? course.name_en : course.name_ar}
                                                                        </StyledTableCell>
                                                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}>
                                                                            {course.teacher?.first_name} {course.teacher?.last_name}
                                                                        </StyledTableCell>
                                                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}>
                                                                            {language === 'en' ? course.category?.name_en : course.category?.name_ar}
                                                                        </StyledTableCell>
                                                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}>
                                                                            {statusLabel(course.status)}
                                                                        </StyledTableCell>
                                                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'} className="text-center">
                                                                            {pathItem ? (language === 'en' ? pathItem.name_en : pathItem.name_ar) : <FormattedMessage id="nothing" />}
                                                                        </StyledTableCell>
                                                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'}>
                                                                            {course.contents?.filter((c) => c.content_type === 'CourseFile').length ?? 0}
                                                                        </StyledTableCell>
                                                                        <StyledTableCell align={language === 'en' ? 'left' : 'right'} className="!text-center" dir="ltr">
                                                                            {course.created_at?.split(' ')[0]}
                                                                        </StyledTableCell>
                                                                    </StyledTableRow>
                                                                ))
                                                            )
                                                        )}
                                                    </TableBody>
                                                </Table>

                                                {/* Pagination Buttons */}
                                                <Box className="flex justify-center items-center" dir="rtl">
                                                    <Button disabled={page + 1 === totalPages} className="cursor-pointer" onClick={() => setPage(currentPage + 1)}>
                                                        <NavigateNextIcon fontSize="large" />
                                                    </Button>
                                                    <Typography variant="body1" className="!text-xl" dir='ltr'>{currentPage + 1} / {totalPages}</Typography>
                                                    <Button disabled={page + 1 === 1} className="cursor-pointer" onClick={() => setPage(currentPage - 1)}>
                                                        <NavigateBeforeIcon fontSize="large" />
                                                    </Button>
                                                </Box>
                                            </TableContainer>
                    </DashboardPanelCard>
                </ScrollReveal>
            </PageContent>

            <ModalOverlay id="details" onClose={() => setPopup('details', 'none')}>
                <CourseDetails onClickClose={() => setPopup('details', 'none')} data={course} onDeleted={deleteCourse} />
            </ModalOverlay>

            <ModalOverlay id="filter" onClose={() => setPopup('filter', 'none')}>
                <CoursesFilter
                    onClickClose={() => setPopup('filter', 'none')}
                    onClickConfirm={filteringCourses}
                    categoriesValue={categoriesValue}
                    fromCount={fromCount}
                    toCount={toCount}
                    fromDate={fromDate}
                    toDate={toDate}
                    pathsValue={pathsValue}
                    filterWait={filterWait}
                    setCategoriesValue={setCategoriesValue}
                    setPathsValue={setPathsValue}
                    setToDate={setToDate}
                    setCategory={setCategory}
                    setPath={setPath}
                    setFromCount={setFromCount}
                    setFromDate={setFromDate}
                    setToCount={setToCount}
                    setFilterWait={setFilterWait}
                />
            </ModalOverlay>

            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default Courses;